import React from 'react';
import clsx from 'clsx';
import { useReducedMotion } from 'framer-motion';
import { IntegrationChip, type ChipAccent } from './IntegrationChip';

export type IntegrationItem = { label: string; accent?: ChipAccent; variant?: 'solid' | 'outline' | 'dashed' };

export const IntegrationsMarquee: React.FC<{
  lineText?: string;
  items: IntegrationItem[];
  speedSec?: number;
  className?: string;
  isFullWidth?: boolean;
}> = ({ lineText, items, speedSec = 20, className, isFullWidth = false }) => {
  const reduce = useReducedMotion();

  // Double track for seamless loop
  const track = React.useMemo(() => [...items, ...items, ...items], [items]);
  const dur = Math.max(2, speedSec);

  if (!items?.length) return null;

  return (
    <div
      className={clsx(
        'relative',
        // If full width, we don't apply the pill shape styles
        !isFullWidth && 'w-full rounded-xl border-2 border-black bg-white/80 backdrop-blur py-2.5 px-3',
        isFullWidth && 'w-full overflow-hidden',
        'pointer-events-none select-none',
        className
      )}
      aria-hidden="true"
    >
      <div className={clsx("flex items-center", isFullWidth ? "justify-center" : "gap-3")}>
        
        {/* Only show lineText if NOT fullWidth (or if you want it inline) - logic allows disabling it by passing null */}
        {!isFullWidth && lineText && (
          <div className="shrink-0 text-slate-900 text-sm md:text-base font-extrabold">
            {lineText}
          </div>
        )}

        <div 
            className="relative flex-1 h-14 flex items-center overflow-hidden"
            style={{
                // UPDATED: Use CSS Mask for perfect edge fading on ANY background color
                maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
                WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)'
            }}
        >
          {/* Track */}
          <div
            className={clsx(
              'absolute flex items-center gap-3 md:gap-6 will-change-transform min-w-max',
              !reduce && 'animate-[marquee_var(--dur)_linear_infinite]'
            )}
            style={reduce ? undefined : ({ ['--dur' as any]: `${dur}s` } as React.CSSProperties)}
          >
            <ul className="flex items-center gap-3 md:gap-6 min-w-max">
              {track.map((it, i) => (
                <li key={`${it.label}-${i}`} className="shrink-0">
                  <IntegrationChip label={it.label} accent={it.accent} variant={it.variant} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Keyframes */}
      {!reduce && (
        <style>
          {`
          @keyframes marquee {
            0% { transform: translate3d(0, 0, 0); }
            100% { transform: translate3d(-33.333%, 0, 0); } /* Adjusted for 3x items */
          }
          `}
        </style>
      )}
    </div>
  );
};

export default IntegrationsMarquee;